import { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import AddressIcon from "src/assets/all-icons/address-icon.jsx";
import CookiesIcon from "src/assets/all-icons/cookies-icon.jsx";
import CreditCardIcon from "src/assets/all-icons/credit-card-icon.jsx";
import EditArtworkIcon from "src/assets/all-icons/edit-artwork-icon.jsx";
import LanguageIcon from "src/assets/all-icons/language-icon.jsx";
import ModeAndThemeIcon from "src/assets/all-icons/mode-and-theme-icon.jsx";
import PrivacyPoliciesIcon from "src/assets/all-icons/privacy-policies-icon.jsx";
import QrCodeIcon from "src/assets/all-icons/qr-code-icon.jsx";
import TermsOfUseIcon from "src/assets/all-icons/terms-of-use-icon.jsx";
import UserIcon from "src/assets/all-icons/user-icon.jsx";

import GalleryIcon from '@/assets/all-icons/gallery-icon';
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import LogoutConfirmation from '@/layouts/navigation/user-settings/components/LogoutConfirmation';
import AuthService from '@/services/auth.service';
import {
  applyPreferredLocale,
  getPreferredLocale,
} from '@/services/locale.service';
import ProfileNotificationsIcon from '@/shared-components/icons/components/user-profile/ProfileNotificationsIcon';
import DeleteAccountDialog from '@/shared-components/popups/components/DeleteAccountDialog';
import Text from '@/shared-components/text/components/Text';

import './styles/SettingsMenu.css';

function SettingsMenuNavLink({ to, Icon, iconClassName, text, replace, value = null }) {
  return (
    <>
      <NavLink 
        className={({ isActive }) =>`settings-menu__menu-link${isActive ? " active" : ""}`} 
        to={to} 
        state={{ from: true }} 
        replace={replace}
      >
        <div className="settings-menu__menu-link-icon-container">
          <Icon className={`settings-menu__menu-link-icon ${iconClassName}`}/>
        </div>

        <Text className="settings-menu__menu-link-text font-size-medium">
          {text}
        </Text>

        {value ? (
          <Text className="settings-menu__menu-link-value font-size-small">
            {value}
          </Text>
        ) : null}
      </NavLink>
    </>
  );
}

export default function SettingsMenu({ replace=false }) {
  const { state } = useStateHandler();
  const tMenu = useTranslations('common.user_settings.menu');
  const tLanguage = useTranslations('common.settings_menu.preferences.language_selector');
  const tPreferences = useTranslations('common.settings_menu.preferences');
  const tNotifications = useTranslations('common.user_settings.communication.notifications');
  const session = state.user_session;
  const isGalleryMember = session?.user_type !== "gallery" && Boolean(session?.gallery_id);
  const [logoutDialog, setLogoutDialog] = useState(false);
  const [ confirmDeletionDialog, setConfirmDeletionDialog] = useState(false);
  const [selectedLocale, setSelectedLocale] = useState(() => getPreferredLocale());
  const navigate = useNavigate();

  useEffect(() => {
    setSelectedLocale(applyPreferredLocale());
  }, []);

  useEffect(() => {
    const handlePreferredLanguageChange = (event) => {
      const nextLocale = event?.detail?.locale;
      if (nextLocale) {
        setSelectedLocale(nextLocale);
      }
    };

    window.addEventListener('suarte:preferred-language-changed', handlePreferredLanguageChange);
    return () => {
      window.removeEventListener('suarte:preferred-language-changed', handlePreferredLanguageChange);
    };
  }, []);

  return (
    <>
      <div className="settings-menu__container">
        <Text className="settings-menu__menu-heading font-size-large">
          {tMenu('sections.account')}
        </Text>

        <div className="settings-menu__section element-non-selectable">
          <SettingsMenuNavLink 
            to="/profile/settings/edit-profile" 
            iconClassName="edit" 
            text={tMenu('links.edit_profile')}
            Icon={EditArtworkIcon}
            replace={replace}
          />

          <SettingsMenuNavLink 
            to="/profile/settings/login" 
            iconClassName="user" 
            text={tMenu('links.log_in')}
            Icon={UserIcon}
            replace={replace}
          />

          {
            state.user_session.user_type === "gallery" ? 
              <>
                <SettingsMenuNavLink 
                  to="/profile/settings/gallery-information" 
                  iconClassName="gallery" 
                  text={tMenu('links.gallery_information')}
                  Icon={GalleryIcon}
                  replace={replace}
                /> 

                <SettingsMenuNavLink 
                  to="/profile/settings/payments-profile" 
                  iconClassName="credit" 
                  text={tMenu('links.payments_profile')}
                  Icon={CreditCardIcon}
                  replace={replace}
                />
              </>
              :
              <>

                <SettingsMenuNavLink 
                  to="/profile/settings/payment-methods" 
                  iconClassName="credit" 
                  text={tMenu('links.payment_methods')}
                  Icon={CreditCardIcon}
                  replace={replace}
                />

                <SettingsMenuNavLink 
                  to="/profile/settings/address" 
                  iconClassName="house" 
                  text={tMenu('links.address')}
                  Icon={AddressIcon}
                  replace={replace}
                />
              </> 
          }
        </div>

        <Text className="settings-menu__menu-heading font-size-large">
          {tMenu('sections.preferences')}
        </Text>

        <div className="settings-menu__section element-non-selectable">
          <SettingsMenuNavLink 
            to="/profile/settings/mode-theme" 
            iconClassName="mode-theme" 
            text={tPreferences('mode_theme')}
            Icon={ModeAndThemeIcon}
            replace={replace}
          />

          {state.user_session.user_type === "gallery" && (
            <SettingsMenuNavLink
              to="/profile/settings/price"
              iconClassName="credit"
              text={tPreferences('price')}
              Icon={CreditCardIcon}
              replace={replace}
            />
          )}

          <SettingsMenuNavLink 
            to="/profile/settings/language" 
            iconClassName="language" 
            text={tLanguage('label')}
            value={selectedLocale.toUpperCase()}
            Icon={LanguageIcon}
            replace={replace}
          />

          <SettingsMenuNavLink
            to="/profile/settings/notifications"
            iconClassName="notifications"
            text={tNotifications('heading')}
            Icon={ProfileNotificationsIcon}
            replace={replace}
          />

          {state.user_session.user_type === "gallery" && (
            <SettingsMenuNavLink
              to="/profile/settings/gallery-qr"
              iconClassName="qr"
              text={tPreferences('gallery_qr.menu_label')}
              Icon={QrCodeIcon}
              replace={replace}
            />
          )}

          {state.user_session.user_type === "artist" && (
            <SettingsMenuNavLink
              to="/profile/settings/artist-qr"
              iconClassName="qr"
              text={tPreferences('artist_qr.menu_label')}
              Icon={QrCodeIcon}
              replace={replace}
            />
          )}
        </div>

        <Text className="settings-menu__menu-heading font-size-large">
          {tMenu('sections.about')}
        </Text>

        <div className="settings-menu__section element-non-selectable">
          <SettingsMenuNavLink 
            to="/profile/settings/privacy" 
            iconClassName="privacy" 
            text="Privacy policies" 
            Icon={PrivacyPoliciesIcon}
            replace={replace}
          />

          <SettingsMenuNavLink
            to="/profile/settings/terms" 
            iconClassName="terms" 
            text="Terms of use" 
            Icon={TermsOfUseIcon}
            replace={replace}
          />

          <SettingsMenuNavLink 
            to="/profile/settings/cookies" 
            iconClassName="cookies" 
            text="Cookies" 
            Icon={CookiesIcon}
            replace={replace}
          />
        </div>

        {state.user_session.user_type === "gallery" && (
          <Text
            className="settings-menu__text-button element-non-selectable font-size-large"
            onClick={()=>{
              navigate("/profile/settings/closed-mode", { state: { from: true }, replace: replace });
            }}>
            {tMenu('actions.closed_mode')}
          </Text>
        )}

        <Text
          className="settings-menu__text-button element-non-selectable font-size-large"
          onClick={()=>setLogoutDialog(true)}>
          {tMenu('actions.log_out')}
        </Text>

        {!isGalleryMember && (
          <Text
            className="settings-menu__text-button element-non-selectable font-size-large"
            onClick={()=>setConfirmDeletionDialog(true)}>
            {tMenu('actions.delete_account')}
          </Text>
        )}
      </div>
      {/* Log out confirmation Popup */}
      <LogoutConfirmation 
        open={logoutDialog} 
        onClose={() => setLogoutDialog(false)} 
        onConfirm={AuthService.logout} 
      />
      <DeleteAccountDialog open={confirmDeletionDialog} setOpen={setConfirmDeletionDialog}  />
    </>
  );
}
